import { useMutation, useQueryClient } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';

import { useNotification } from '@/components/notifications';
import { WORKFLOW_PLACEMENT_QUERY_KEY } from '@/hooks/queries/use-workflow-placement';
import {
  ApprovalDecision,
  ApprovalSide,
  Review,
  submitDecision,
} from '@/services/workflow/qualification';

interface UseSubmitDecisionOptions {
  orgId: string | undefined;
  workflowId: string;
  attemptId: string | undefined;
  onSettled?: (review: Review) => void;
}

export const useSubmitDecision = ({
  orgId,
  workflowId,
  attemptId,
  onSettled,
}: UseSubmitDecisionOptions) => {
  const queryClient = useQueryClient();
  const notification = useNotification();

  return useMutation<
    Review,
    Error,
    { decision: ApprovalDecision; side: ApprovalSide; feedback?: string }
  >({
    mutationFn: ({ decision, side, feedback }) => {
      if (!attemptId) throw new Error('No attempt to decide on');
      return submitDecision(attemptId, decision, side, feedback);
    },
    onSuccess: (review) => {
      // An approval can settle the gate and promote the copy out of this
      // org entirely, so the placement this screen was built from may no
      // longer exist.
      queryClient.invalidateQueries({
        queryKey: [WORKFLOW_PLACEMENT_QUERY_KEY, orgId, workflowId],
      });
      notification.success(
        review.outstanding_sides.length
          ? 'Recorded. Still waiting on the other side.'
          : 'Recorded.',
      );
      onSettled?.(review);
    },
    onError: (error) => {
      notification.error(error.message || 'Could not record the decision');
      Sentry.captureException(error, {
        extra: { context: 'submit-decision', attemptId, workflowId },
      });
    },
  });
};
